import {useRef} from 'react';

import {Alert, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {usePaymentEvents} from '../hooks/usePaymentEvents';
import {usePaymentEventsTableColumns} from '../hooks/usePaymentEventsTableColumns';
import {PaymentEvent} from '../types';

interface RecordsTableProps {
    invoiceId?: string;
}

export const RecordsTable = ({invoiceId}: RecordsTableProps) => {

    const {
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
        handleFilterByKeyword,
    } = usePaymentEvents(invoiceId);

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const {columns} = usePaymentEventsTableColumns();

    const term = keyword.trim();

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load payment events')}>
                    {I18n.get('The billing API did not return this invoice\'s payment events. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            {/*
              NO ROW SELECTION and NO ROW CLICK, deliberately. Payment events are
              read-only: billing-api has no public mutation for them (rows are written
              by the Stripe webhook, FNP-339), so there is no batch action for a
              checkbox to drive. There is no payment-event detail view yet, so a row
              click would have nowhere to go.
            */}
            <MantineDataTable<PaymentEvent>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope
                // as the keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            {/*
              TWO EMPTY STATES (§3, §13). "No payment events yet" is normal for an
              invoice nobody has paid; "nothing matches" means the search is too
              narrow. Gated on !loading so it does not flash during the first fetch,
              and on !isError so a failed load keeps its own Alert rather than
              claiming there are none.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert
                    color={term ? 'gray' : 'blue'}
                    title={term ? I18n.get('No matching payment events') : I18n.get('No payment events yet')}
                >
                    {term
                        ? `${I18n.get('Nothing in the loaded payment events matches')} "${term}". ${I18n.get('Clear the search to see every loaded row. A match may still be on a page that has not been loaded yet.')}`
                        : I18n.get('Payment events are recorded from Stripe when this invoice is paid, fails or is refunded. None has been recorded for it yet, so this is not an error.')}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'payment event'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
